/**
 * Estate Flow - Property Management Platform
 * Copyright © 2025 Sebastian Alliance Group LLC. All Rights Reserved.
 * 
 * PROPRIETARY AND CONFIDENTIAL
 * 
 * This software and associated documentation files (the "Software") are the 
 * proprietary property of Sebastian Alliance Group LLC and are protected by 
 * copyright, trademark, and other intellectual property laws.
 * 
 * Unauthorized copying, modification, distribution, or use of this Software,
 * via any medium, is strictly prohibited without express written permission
 * from Sebastian Alliance Group LLC.
 * 
 * For licensing inquiries: legal@sebastianalliancegroup.com
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Building2, Users, TrendingUp, CheckCircle, Globe, Shield, Zap, Home, UserCheck, DollarSign, Menu, X, Calendar, FileText, Palmtree, Gift } from 'lucide-react';
import PricingCalculator from './PricingCalculator';
import LanguageSwitcher from './LanguageSwitcher';
import FinancingPartnerBanner from './financing/FinancingPartnerBanner';

const LandingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref');
  const { t } = useTranslation();
  const [activeFeature, setActiveFeature] = useState(0);
  const [selectedAccountType, setSelectedAccountType] = useState('landlord');
  const [showCalculator, setShowCalculator] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Helper: navigate with referral code preserved
  const navigateWithRef = (path) => {
    if (refCode) {
      const separator = path.includes('?') ? '&' : '?';
      navigate(`${path}${separator}ref=${refCode}`);
    } else {
      navigate(path);
    }
  };

  const features = [
    {
      icon: <Building2 className="h-8 w-8" />,
      titleKey: 'landing.propertyManagement',
      descKey: 'landing.propertyManagementDesc',
      color: 'text-emerald-600'
    },
    {
      icon: <Users className="h-8 w-8" />,
      titleKey: 'landing.tenantPortal',
      descKey: 'landing.tenantPortalDesc',
      color: 'text-blue-600'
    },
    {
      icon: <TrendingUp className="h-8 w-8" />,
      titleKey: 'landing.financialAnalytics',
      descKey: 'landing.financialAnalyticsDesc',
      color: 'text-purple-600'
    }
  ];

  const pricingTiers = [
    {
      type: 'Landlord',
      subtitle: 'Perfect for individual property owners',
      price: '$162',
      setupFee: 'Setup fee',
      monthly: 'From $161/month',
      features: [
        'Up to 100 units',
        'Tenant portal',
        'Payment processing',
        'Financial reporting',
        'Email support'
      ],
      badge: 'Popular',
      badgeColor: 'bg-emerald-500'
    },
    {
      type: 'HOA Community',
      subtitle: 'Comprehensive community management',
      price: '$323',
      setupFee: 'Setup fee',
      monthly: 'From $269/month',
      features: [
        'Up to 200 units',
        'Voting system',
        'Asset management',
        'Financial reports',
        'Vendor management',
        'Work orders',
        'Board portal'
      ],
      badge: 'Best Value',
      badgeColor: 'bg-blue-500'
    },
    {
      type: 'Vacation Rentals',
      subtitle: 'Short-term rental management',
      price: '$99',
      setupFee: 'Setup fee',
      monthly: 'From $19/month',
      features: [
        '1-3 properties: $19/mo',
        '4-10 properties: $69/mo',
        '11-25 properties: $199/mo',
        '26-50 properties: $599/mo',
        'Channel manager included',
        '4% transaction fees'
      ],
      badge: 'NEW',
      badgeColor: 'bg-orange-500'
    },
    {
      type: 'Property Manager',
      subtitle: 'White-label solution for professionals',
      price: '$1,499',
      setupFee: 'Setup fee',
      monthly: '$215/month',
      features: [
        'Unlimited properties',
        'White-label branding',
        'Client sub-portals',
        'Advanced analytics',
        'Priority support',
        '2% transaction fees'
      ],
      badge: 'Enterprise',
      badgeColor: 'bg-purple-500'
    }
  ];

  const userTypes = [
    {
      icon: <Building2 className="h-12 w-12 text-emerald-600" />,
      titleKey: 'landing.condoAssociations',
      descKey: 'landing.condoAssociationsDesc',
      image: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=400&h=250&fit=crop'
    },
    {
      icon: <Home className="h-12 w-12 text-blue-600" />,
      titleKey: 'landing.apartmentBuildings',
      descKey: 'landing.apartmentBuildingsDesc',
      image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=400&h=250&fit=crop'
    },
    {
      icon: <UserCheck className="h-12 w-12 text-purple-600" />,
      titleKey: 'landing.landlordsManagers',
      descKey: 'landing.landlordsManagersDesc',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400&h=250&fit=crop'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [features.length]);

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo - Mobile Responsive */}
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src="/estate-flow-logo.png" 
                alt="Estate Flow Logo" 
                className="h-10 w-auto sm:h-12"
              />
              <span className="text-xl sm:text-2xl font-bold text-gray-900">Estate Flow</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              <LanguageSwitcher variant="compact" />
              <Button 
                variant="ghost" 
                onClick={() => navigate('/kb')}
                data-testid="help-center-nav-btn"
              >
                Help Center
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/faq')}
              >
                {t('nav.faq')}
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/tenant/login')}
                data-testid="tenant-portal-nav-btn"
              >
                {t('nav.tenantPortal')}
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/property-manager/login')}
                data-testid="pm-portal-nav-btn"
              >
                {t('nav.managerPortal')}
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/vacation-rentals/search')}
                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                data-testid="vacation-rentals-nav-btn"
              >
                Vacation Rentals
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/rentals')}
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                data-testid="rental-marketplace-nav-btn"
              >
                Leasing Hub
              </Button>
              <Button 
                onClick={() => navigateWithRef('/onboarding')}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="start-free-trial-btn"
              >
                {t('common.getStarted')}
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200">
              <div className="px-2 pt-2 pb-3 space-y-1">
                {/* Mobile Language Switcher */}
                <div className="px-3 py-2">
                  <LanguageSwitcher variant="default" className="w-full" />
                </div>
                <button
                  onClick={() => {
                    navigate('/kb');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                >
                  Help Center
                </button>
                <button
                  onClick={() => {
                    navigate('/faq');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                >
                  {t('nav.faq')}
                </button>
                <button
                  onClick={() => {
                    navigate('/vacation-rentals/search');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
                >
                  Vacation Rentals
                </button>
                <button
                  onClick={() => {
                    navigate('/rentals');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                >
                  Leasing Hub
                </button>
                <button
                  onClick={() => {
                    navigate('/tenant/login');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                >
                  {t('nav.tenantPortal')}
                </button>
                <button
                  onClick={() => {
                    navigate('/property-manager/login');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                >
                  {t('nav.managerPortal')}
                </button>
                <button
                  onClick={() => {
                    navigate('/landlord/login');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                >
                  {t('nav.landlordLogin')}
                </button>
                <button
                  onClick={() => {
                    navigate('/hoa-admin/login');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                >
                  {t('nav.hoaAdminLogin')}
                </button>
                <button
                  onClick={() => {
                    navigateWithRef('/onboarding');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-white bg-emerald-600 hover:bg-emerald-700"
                >
                  {t('common.getStarted')}
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Referral Banner */}
      {refCode && (
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-2.5 px-4 text-center" data-testid="referral-banner">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-sm">
            <Gift className="h-4 w-4" />
            <span>You've been referred! Code <strong>{refCode}</strong> will be applied at signup.</span>
            <span className="hidden sm:inline">Browse our solutions below and get started.</span>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Hero Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1920&h=800&fit=crop&crop=center" 
            alt="Modern Caribbean condos and apartments"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/80 to-blue-900/60" />
        </div>
        
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center">
            <Badge className="mb-6 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
              <Globe className="w-4 h-4 mr-2" />
              {t('landing.trustedBy')}
            </Badge>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              {t('landing.heroTitle')}
            </h1>
            <p className="text-xl text-gray-100 mb-8 max-w-3xl mx-auto leading-relaxed">
              {t('landing.heroSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg" 
                onClick={() => navigateWithRef('/onboarding')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid="hero-get-started-btn"
              >
                {t('common.getStarted')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="px-8 py-3 rounded-xl border-2 border-white text-white hover:bg-white hover:text-gray-900 transition-all duration-300"
                onClick={() => navigate('/demo')}
                data-testid="view-demo-btn"
              >
                {t('landing.watchDemo')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* User Types Grid Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="user-types-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('landing.perfectFor')}</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('landing.whyChooseSubtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {userTypes.map((type, index) => (
              <Card 
                key={index} 
                className="group hover:shadow-xl transition-all duration-300 cursor-pointer border-2 hover:border-emerald-200 overflow-hidden"
                onClick={() => {
                  const path = '/onboarding';
                  const state = { selectedType: t(type.titleKey).toLowerCase().split(' ')[0] };
                  if (refCode) {
                    navigate(`${path}?ref=${refCode}`, { state });
                  } else {
                    navigate(path, { state });
                  }
                }}
                data-testid={`user-type-card-${index}`}
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={type.image}
                    alt={t(type.titleKey)}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    {type.icon}
                    <h3 className="text-xl font-semibold text-gray-900 ml-3">{t(type.titleKey)}</h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed mb-4">{t(type.descKey)}</p>
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                    {t('common.getStarted')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('landing.whyChoose')}</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('landing.whyChooseSubtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className={`p-8 border-2 transition-all duration-500 cursor-pointer hover:shadow-xl ${
                  activeFeature === index ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'
                }`}
                onMouseEnter={() => setActiveFeature(index)}
                data-testid={`feature-card-${index}`}
              >
                <div className={`${feature.color} mb-4`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t(feature.titleKey)}</h3>
                <p className="text-gray-600 leading-relaxed">{t(feature.descKey)}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Problem → Solution Section - What Makes Us Different */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="why-different-section">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-purple-100 text-purple-700 hover:bg-purple-100">
              Built for Real Property Managers
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Finally, software that gets it
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We built Estate Flow because we were tired of the same problems you face every day.
            </p>
          </div>

          {/* Problem → Solution Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {/* Card 1: Tax Headaches */}
            <div className="group bg-white rounded-2xl border-2 border-gray-100 hover:border-emerald-200 p-6 transition-all duration-300 hover:shadow-xl">
              <div className="mb-4 w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <FileText className="h-6 w-6 text-emerald-600" />
              </div>
              <h3 className="text-sm font-medium text-gray-400 mb-1 line-through decoration-gray-300">
                Tax season is a nightmare
              </h3>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                P&L reports in 2 clicks
              </h3>
              <p className="text-gray-600 mb-4">
                Generate quarterly tax reports with the exact forms your accountant needs. 
                VAT, Sales Tax, GST - we handle it all.
              </p>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <div className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 mr-2 text-emerald-500" />
                  <span>10 countries • Auto tax forms • Filing reminders</span>
                </div>
              </div>
            </div>

            {/* Card 2: No Visibility */}
            <div className="group bg-white rounded-2xl border-2 border-gray-100 hover:border-emerald-200 p-6 transition-all duration-300 hover:shadow-xl">
              <div className="mb-4 w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="text-sm font-medium text-gray-400 mb-1 line-through decoration-gray-300">
                Which property is actually profitable?
              </h3>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                See it all at a glance
              </h3>
              <p className="text-gray-600 mb-4">
                Beautiful charts show revenue vs expenses, property comparisons, 
                and occupancy trends. No more guessing.
              </p>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <div className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 mr-2 text-emerald-500" />
                  <span>Visual analytics • Property comparison • Trends</span>
                </div>
              </div>
            </div>

            {/* Card 3: Global Properties */}
            <div className="group bg-white rounded-2xl border-2 border-gray-100 hover:border-emerald-200 p-6 transition-all duration-300 hover:shadow-xl">
              <div className="mb-4 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Globe className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-sm font-medium text-gray-400 mb-1 line-through decoration-gray-300">
                My software only works in the US
              </h3>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Bahamas, UK, Canada & more
              </h3>
              <p className="text-gray-600 mb-4">
                Manage properties across borders. Multi-currency payouts, 
                local tax compliance, and region-specific forms.
              </p>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <div className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 mr-2 text-emerald-500" />
                  <span>VAT • GST/HST • Sales Tax • EU compliance</span>
                </div>
              </div>
            </div>

            {/* Card 4: Cash Flow */}
            <div className="group bg-white rounded-2xl border-2 border-gray-100 hover:border-emerald-200 p-6 transition-all duration-300 hover:shadow-xl">
              <div className="mb-4 w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-amber-600" />
              </div>
              <h3 className="text-sm font-medium text-gray-400 mb-1 line-through decoration-gray-300">
                I never know my actual cash position
              </h3>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                6-month cash flow forecast
              </h3>
              <p className="text-gray-600 mb-4">
                Know exactly what's coming in and going out. Plan repairs, 
                anticipate vacancies, and sleep better at night.
              </p>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <div className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 mr-2 text-emerald-500" />
                  <span>Projections • Vacancy cost • Cash planning</span>
                </div>
              </div>
            </div>

            {/* Card 5: Tax Savings */}
            <div className="group bg-white rounded-2xl border-2 border-gray-100 hover:border-emerald-200 p-6 transition-all duration-300 hover:shadow-xl">
              <div className="mb-4 w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Shield className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-sm font-medium text-gray-400 mb-1 line-through decoration-gray-300">
                Am I missing deductions?
              </h3>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Tax savings finder
              </h3>
              <p className="text-gray-600 mb-4">
                Our system flags missing receipts, suggests depreciation strategies, 
                and estimates your quarterly payments.
              </p>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <div className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 mr-2 text-emerald-500" />
                  <span>Deduction alerts • 1031 tips • Filing deadlines</span>
                </div>
              </div>
            </div>

            {/* Card 6: Collection */}
            <div className="group bg-white rounded-2xl border-2 border-gray-100 hover:border-emerald-200 p-6 transition-all duration-300 hover:shadow-xl">
              <div className="mb-4 w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                <Zap className="h-6 w-6 text-teal-600" />
              </div>
              <h3 className="text-sm font-medium text-gray-400 mb-1 line-through decoration-gray-300">
                Chasing late payments is exhausting
              </h3>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                96%+ collection rate
              </h3>
              <p className="text-gray-600 mb-4">
                Automated reminders, online payments, and real-time tracking. 
                See exactly who's paid and who hasn't.
              </p>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <div className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 mr-2 text-emerald-500" />
                  <span>Auto reminders • Online pay • Payment tracking</span>
                </div>
              </div>
            </div>
          </div>

          {/* Social Proof Bar */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 text-white">
            <div className="grid md:grid-cols-4 gap-8 text-center">
              <div>
                <p className="text-4xl font-bold text-emerald-400">$12M+</p>
                <p className="text-gray-400 mt-1">Rent collected</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-emerald-400">2,400+</p>
                <p className="text-gray-400 mt-1">Units managed</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-emerald-400">10</p>
                <p className="text-gray-400 mt-1">Countries supported</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-emerald-400">8 hrs</p>
                <p className="text-gray-400 mt-1">Saved per month on tax prep</p>
              </div>
            </div>
          </div>

          {/* What We Do Differently - Subtle Comparison */}
          <div className="mt-20">
            <div className="text-center mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">What you won't find elsewhere</h3>
              <p className="text-gray-600">Features that actually matter for property managers</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="flex items-start p-4 bg-emerald-50 rounded-xl">
                <CheckCircle className="h-6 w-6 text-emerald-600 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900">Multi-country tax forms</p>
                  <p className="text-sm text-gray-600">VAT, GST, Sales Tax - auto-generated</p>
                </div>
              </div>
              <div className="flex items-start p-4 bg-emerald-50 rounded-xl">
                <CheckCircle className="h-6 w-6 text-emerald-600 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900">Visual analytics</p>
                  <p className="text-sm text-gray-600">Charts & graphs, not just tables</p>
                </div>
              </div>
              <div className="flex items-start p-4 bg-emerald-50 rounded-xl">
                <CheckCircle className="h-6 w-6 text-emerald-600 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900">Property-by-property P&L</p>
                  <p className="text-sm text-gray-600">Know which ones make money</p>
                </div>
              </div>
              <div className="flex items-start p-4 bg-emerald-50 rounded-xl">
                <CheckCircle className="h-6 w-6 text-emerald-600 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900">Tax savings alerts</p>
                  <p className="text-sm text-gray-600">Never miss a deduction again</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Calculator Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-emerald-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Calculate Your Pricing</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Use our pricing calculator to see exactly what you'll pay based on your portfolio size. 
              No hidden fees, no surprises - just transparent pricing tailored to your needs.
            </p>
            
            {/* Account Type Selector */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              <Button
                variant={selectedAccountType === 'landlord' ? 'default' : 'outline'}
                onClick={() => setSelectedAccountType('landlord')}
                className={selectedAccountType === 'landlord' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              >
                <Home className="h-4 w-4 mr-2" />
                Landlord
              </Button>
              <Button
                variant={selectedAccountType === 'hoa' ? 'default' : 'outline'}
                onClick={() => setSelectedAccountType('hoa')}
                className={selectedAccountType === 'hoa' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              >
                <Building2 className="h-4 w-4 mr-2" />
                HOA Community
              </Button>
              <Button
                variant={selectedAccountType === 'vacation_rental' ? 'default' : 'outline'}
                onClick={() => setSelectedAccountType('vacation_rental')}
                className={selectedAccountType === 'vacation_rental' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              >
                <Palmtree className="h-4 w-4 mr-2" />
                Vacation Rentals
              </Button>
              <Button
                variant={selectedAccountType === 'property_manager' ? 'default' : 'outline'}
                onClick={() => setSelectedAccountType('property_manager')}
                className={selectedAccountType === 'property_manager' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              >
                <Users className="h-4 w-4 mr-2" />
                Property Manager
              </Button>
            </div>
          </div>

          {/* Pricing Calculator Component */}
          <PricingCalculator 
            accountType={selectedAccountType}
            onGetStarted={(type, units, buildings, pricing) => {
              const path = refCode ? `/onboarding?ref=${refCode}` : '/onboarding';
              navigate(path, { 
                state: { 
                  selectedType: type,
                  units,
                  buildings,
                  calculatedPricing: pricing
                } 
              });
            }}
          />
        </div>
      </section>

      {/* Vacation Rentals Section - NEW */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-emerald-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div>
              <Badge className="mb-4 bg-emerald-500 text-white hover:bg-emerald-600">
                NEW FEATURE
              </Badge>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Vacation Rentals Management
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                Maximize your short-term rental income with our all-in-one vacation property management solution.
              </p>
              
              {/* Features Grid */}
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <Globe className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Channel Sync</h4>
                    <p className="text-sm text-gray-500">Expedia, Booking.com, VRBO</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <DollarSign className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Only 2% Fee</h4>
                    <p className="text-sm text-gray-500">vs 15-20% OTA commission</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-purple-100 p-2 rounded-lg">
                    <Calendar className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Dynamic Pricing</h4>
                    <p className="text-sm text-gray-500">Auto-adjust rates by demand</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-orange-100 p-2 rounded-lg">
                    <Users className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Book Direct Widget</h4>
                    <p className="text-sm text-gray-500">Guests save 10-15%</p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => navigate('/vacation-rentals/search')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-lg px-8 py-6"
                >
                  Explore Vacation Rentals
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate(refCode ? `/onboarding?role=vacation_rental&ref=${refCode}` : '/onboarding?role=vacation_rental')}
                  className="text-lg px-8 py-6"
                >
                  List Your Property
                </Button>
              </div>
            </div>
            
            {/* Visual Card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full mb-4">
                  <span className="text-xl">🎉</span>
                  <span className="font-semibold">Book Direct & Save</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Your Unfair Advantage</h3>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">OTA Commission</span>
                  <span className="text-red-500 font-bold line-through">15-20%</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg border-2 border-emerald-200">
                  <span className="text-emerald-700 font-medium">Estate Flow Fee</span>
                  <span className="text-emerald-600 font-bold text-xl">Only 2%</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                  <span className="text-blue-700">Guest Direct Discount</span>
                  <span className="text-blue-600 font-bold">10-15% OFF</span>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                <p className="text-gray-600 mb-2">Global Coverage, Caribbean Focus</p>
                <p className="text-sm text-gray-500">USA • Canada • Dubai • Caribbean • 40+ Countries Supported</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
              Simple Pricing
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Plan</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Transparent pricing with no hidden fees. Start managing your properties today.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mt-8">
            {pricingTiers.map((tier, index) => (
              <Card 
                key={index} 
                className="relative pt-12 pb-8 px-8 border-2 border-gray-200 hover:border-emerald-400 hover:shadow-xl transition-all duration-300 pricing-card flex flex-col h-full"
                data-testid={`pricing-card-${tier.type.toLowerCase().replace(' ', '-')}`}
              >
                {tier.badge && (
                  <Badge className={`absolute top-4 left-1/2 transform -translate-x-1/2 ${tier.badgeColor} text-white px-4 py-1`}>
                    {tier.badge}
                  </Badge>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.type}</h3>
                  <p className="text-gray-600 mb-4">{tier.subtitle}</p>
                  
                  {/* Pricing */}
                  <div className="mb-2">
                    <span className="text-4xl font-bold text-emerald-600">{tier.price}</span>
                    <span className="text-gray-600 ml-2">{tier.setupFee}</span>
                  </div>
                  
                  {/* Monthly pricing */}
                  <div className="text-sm text-gray-500">
                    <span className="text-emerald-600 font-bold text-base">{tier.monthly}</span>
                  </div>
                </div>
                
                <ul className="space-y-3 mb-8 flex-grow">
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-700">
                      <CheckCircle className="h-5 w-5 text-emerald-500 mr-3 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <div className="mt-auto">
                  <Button 
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl transition-all duration-300"
                    onClick={() => {
                      const path = refCode ? `/onboarding?ref=${refCode}` : '/onboarding';
                      navigate(path, { state: { selectedType: tier.type.toLowerCase() } });
                    }}
                    data-testid={`select-plan-${tier.type.toLowerCase().replace(' ', '-')}`}
                  >
                    Get Started
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center">
              <Shield className="h-12 w-12 text-emerald-600 mb-4" />
              <h4 className="font-semibold text-gray-900 mb-2">Bank-Level Security</h4>
              <p className="text-gray-600">Your data is protected with enterprise-grade encryption</p>
            </div>
            <div className="flex flex-col items-center">
              <Zap className="h-12 w-12 text-emerald-600 mb-4" />
              <h4 className="font-semibold text-gray-900 mb-2">Fast Setup</h4>
              <p className="text-gray-600">Get up and running in less than 10 minutes</p>
            </div>
            <div className="flex flex-col items-center">
              <Globe className="h-12 w-12 text-emerald-600 mb-4" />
              <h4 className="font-semibold text-gray-900 mb-2">Global Reach</h4>
              <p className="text-gray-600">Caribbean-focused with worldwide support across 40+ countries</p>
            </div>
          </div>
        </div>
      </section>

      {/* Login Portals Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Access Your Portal</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Sign in to your dedicated portal based on your role
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Landlord Portal */}
            <Card className="group hover:shadow-xl transition-all duration-300 cursor-pointer border-2 hover:border-blue-400 overflow-hidden">
              <CardContent className="p-8 text-center">
                <div className="mb-6 flex justify-center">
                  <div className="p-4 bg-blue-100 rounded-full group-hover:bg-blue-200 transition-colors">
                    <Home className="h-8 w-8 text-blue-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Landlord Portal</h3>
                <p className="text-gray-600 mb-6">Manage your rental properties</p>
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  onClick={() => navigate('/landlord/login')}
                  data-testid="landlord-portal-btn"
                >
                  Sign In
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>

            {/* Property Manager Portal */}
            <Card className="group hover:shadow-xl transition-all duration-300 cursor-pointer border-2 hover:border-purple-400 overflow-hidden">
              <CardContent className="p-8 text-center">
                <div className="mb-6 flex justify-center">
                  <div className="p-4 bg-purple-100 rounded-full group-hover:bg-purple-200 transition-colors">
                    <Building2 className="h-8 w-8 text-purple-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Property Manager</h3>
                <p className="text-gray-600 mb-6">Professional property management</p>
                <Button 
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  onClick={() => navigate('/property-manager/login')}
                  data-testid="pm-portal-btn"
                >
                  Sign In
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>

            {/* HOA Admin Portal */}
            <Card className="group hover:shadow-xl transition-all duration-300 cursor-pointer border-2 hover:border-indigo-400 overflow-hidden">
              <CardContent className="p-8 text-center">
                <div className="mb-6 flex justify-center">
                  <div className="p-4 bg-indigo-100 rounded-full group-hover:bg-indigo-200 transition-colors">
                    <Users className="h-8 w-8 text-indigo-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">HOA Admin</h3>
                <p className="text-gray-600 mb-6">HOA community management</p>
                <Button 
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => navigate('/hoa-admin/login')}
                  data-testid="hoa-admin-portal-btn"
                >
                  Sign In
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>

            {/* HOA Owner Portal */}
            <Card className="group hover:shadow-xl transition-all duration-300 cursor-pointer border-2 hover:border-teal-400 overflow-hidden">
              <CardContent className="p-8 text-center">
                <div className="mb-6 flex justify-center">
                  <div className="p-4 bg-teal-100 rounded-full group-hover:bg-teal-200 transition-colors">
                    <UserCheck className="h-8 w-8 text-teal-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">HOA Owner</h3>
                <p className="text-gray-600 mb-6">Homeowner portal access</p>
                <Button 
                  className="w-full bg-teal-600 hover:bg-teal-700"
                  onClick={() => navigate('/hoa-owner/login')}
                  data-testid="hoa-owner-portal-btn"
                >
                  Sign In
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>

            {/* Tenant Portal */}
            <Card className="group hover:shadow-xl transition-all duration-300 cursor-pointer border-2 hover:border-cyan-400 overflow-hidden">
              <CardContent className="p-8 text-center">
                <div className="mb-6 flex justify-center">
                  <div className="p-4 bg-cyan-100 rounded-full group-hover:bg-cyan-200 transition-colors">
                    <Home className="h-8 w-8 text-cyan-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Tenant Portal</h3>
                <p className="text-gray-600 mb-6">Access your rental dashboard</p>
                <Button 
                  className="w-full bg-cyan-600 hover:bg-cyan-700"
                  onClick={() => navigate('/tenant/login')}
                  data-testid="tenant-portal-btn"
                >
                  Sign In
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-emerald-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Transform Your Business?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join hundreds of property managers already using Estate Flow to streamline their operations
          </p>
          <Button 
            size="lg" 
            onClick={() => navigateWithRef('/onboarding')}
            className="bg-white text-emerald-600 hover:bg-gray-100 px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
            data-testid="cta-get-started-btn"
          >
            Get Started Today
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Free Tools Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-amber-500 text-white mb-4 px-4 py-1 text-sm">100% FREE</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Free Property Management Tools
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Try our free tools with no signup required. Perfect for landlords and property managers who want to get organized.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Free Maintenance Log */}
            <Card className="border-2 border-amber-200 hover:border-amber-400 hover:shadow-xl transition-all duration-300 overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-white">
                  <div className="flex items-center justify-between">
                    <h3 className="text-2xl font-bold">Free Maintenance Log</h3>
                    <Badge className="bg-white text-amber-600">FREE</Badge>
                  </div>
                  <p className="mt-2 text-amber-100">Track all maintenance requests in one place</p>
                </div>
                <div className="p-6">
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center text-gray-700">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      Track maintenance requests by property
                    </li>
                    <li className="flex items-center text-gray-700">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      Export to CSV anytime
                    </li>
                    <li className="flex items-center text-gray-700">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      Add your company branding
                    </li>
                    <li className="flex items-center text-gray-700">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      No account required
                    </li>
                  </ul>
                  <Button 
                    onClick={() => navigate('/free-tools/maintenance-log')}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                  >
                    Start Tracking Free
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Free Lease Expiry Tracker */}
            <Card className="border-2 border-blue-200 hover:border-blue-400 hover:shadow-xl transition-all duration-300 overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-6 text-white">
                  <div className="flex items-center justify-between">
                    <h3 className="text-2xl font-bold">Free Lease Expiry Tracker</h3>
                    <Badge className="bg-white text-blue-600">FREE</Badge>
                  </div>
                  <p className="mt-2 text-blue-100">Never miss a lease renewal again</p>
                </div>
                <div className="p-6">
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center text-gray-700">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      Track all lease expiry dates
                    </li>
                    <li className="flex items-center text-gray-700">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      Email reminders at 90, 60, 30 days
                    </li>
                    <li className="flex items-center text-gray-700">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      Export to CSV anytime
                    </li>
                    <li className="flex items-center text-gray-700">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      Add your company branding
                    </li>
                  </ul>
                  <Button 
                    onClick={() => navigate('/free-tools/lease-tracker')}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    Start Tracking Free
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Become a Referral Agent CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-emerald-600">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-6">
            <DollarSign className="h-8 w-8 text-emerald-600" />
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">
            Become a Referral Agent
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Earn 25% recurring commission on every person you refer to Estate Flow. 
            Share your unique link and start earning lifetime passive income today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
            <div className="flex items-center text-white">
              <CheckCircle className="h-6 w-6 mr-2 text-emerald-300" />
              <span>25% Lifetime Commission</span>
            </div>
            <div className="flex items-center text-white">
              <CheckCircle className="h-6 w-6 mr-2 text-emerald-300" />
              <span>$50 Minimum Payout</span>
            </div>
            <div className="flex items-center text-white">
              <CheckCircle className="h-6 w-6 mr-2 text-emerald-300" />
              <span>Instant Tracking</span>
            </div>
          </div>
          <Button 
            size="lg"
            onClick={() => navigate('/become-partner')}
            className="bg-white text-emerald-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300"
          >
            Start Earning Today
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <p className="text-sm text-blue-100 mt-4">
            Already a referral partner? <button onClick={() => navigate('/referral/login')} className="underline font-semibold hover:text-white">Sign in to your dashboard</button>
          </p>
        </div>
      </section>

      {/* Real Estate Financing Banner - Wellington Woods & Associates */}
      <FinancingPartnerBanner variant="footer" />

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Building2 className="h-6 w-6" />
                <div>
                  <span className="text-xl font-bold">Estate Flow</span>
                  <p className="text-xs text-gray-400">Where Property Meets Simplicity</p>
                </div>
              </div>
              <p className="text-gray-400 mb-4">
                Complete property management solution for condos, apartments, and HOAs.
              </p>
              <div className="text-sm text-gray-500 space-y-2">
                <p>
                  <strong>Sebastian Alliance Group LLC</strong><br />
                  Registered in the United States<br />
                  Global Payout Network: US & International
                </p>
                <p className="border-t border-gray-700 pt-2">
                  <strong>Supported Currencies:</strong><br />
                  USD, BSD, JMD, BBD, TTD, EUR, GBP
                </p>
                <p className="border-t border-gray-700 pt-2">
                  <strong>Mailing Address:</strong><br />
                  101 N.E. 3rd Ave, STE 1500<br />
                  Fort Lauderdale, Florida 33301
                </p>
                <p className="border-t border-gray-700 pt-2">
                  <strong>Customer Support:</strong><br />
                  Email: ecommerce@sebasalliance.net<br />
                  Phone: 1-877-268-4779
                </p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Login Portals</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <button 
                    onClick={() => navigate('/admin/login')}
                    className="hover:text-emerald-400 transition-colors"
                  >
                    Admin Portal
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/landlord/login')}
                    className="hover:text-emerald-400 transition-colors"
                  >
                    Landlord Portal
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/property-manager/login')}
                    className="hover:text-emerald-400 transition-colors"
                  >
                    Property Manager
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">More Portals</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <button 
                    onClick={() => navigate('/hoa-admin/login')}
                    className="hover:text-emerald-400 transition-colors"
                  >
                    HOA Admin
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/hoa-owner/login')}
                    className="hover:text-emerald-400 transition-colors"
                  >
                    HOA Owner
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/tenant/login')}
                    className="hover:text-emerald-400 transition-colors"
                  >
                    Tenant Portal
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal & Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <button 
                    onClick={() => navigate('/kb')}
                    className="hover:text-emerald-400 transition-colors flex items-center gap-1"
                    data-testid="footer-help-center-link"
                  >
                    Help Center
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/faq')}
                    className="hover:text-emerald-400 transition-colors"
                  >
                    FAQ
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/privacy-policy')}
                    className="hover:text-emerald-400 transition-colors"
                  >
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/terms-conditions')}
                    className="hover:text-emerald-400 transition-colors"
                  >
                    Terms & Conditions
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/refund-policy')}
                    className="hover:text-emerald-400 transition-colors"
                  >
                    Refund Policy
                  </button>
                </li>
              </ul>
            </div>
          </div>
          
          {/* BBB Accreditation Section - Premium Display */}
          <div className="border-t border-gray-800 mt-8 pt-8 pb-6">
            <div className="max-w-5xl mx-auto">
              <a 
                href="https://www.bbb.org/us/fl/fort-lauderdale/profile/information-technology-services/sebastian-alliance-group-llc-0633-92048929" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block bg-gradient-to-r from-blue-50 to-gray-50 rounded-2xl p-8 shadow-lg border-2 border-blue-200 hover:shadow-xl hover:border-blue-300 transition-all duration-300"
              >
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  {/* Left: BBB Badge */}
                  <div className="flex-shrink-0">
                    <img 
                      src="/bbb-a-plus.png" 
                      alt="BBB A+ Accredited Business - Click to verify" 
                      className="h-32 md:h-40 w-auto rounded-lg shadow-md"
                    />
                  </div>
                  
                  {/* Right: Text Content */}
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                      BBB Accredited Business
                    </h3>
                    <div className="flex items-center justify-center md:justify-start gap-2 mb-3">
                      <div className="text-4xl font-bold text-blue-600">A+</div>
                      <div className="text-lg text-gray-600">Rating</div>
                    </div>
                    <p className="text-gray-700 text-sm md:text-base leading-relaxed mb-3">
                      Sebastian Alliance Group has earned BBB® Accreditation with an <strong>A+ Rating</strong>, 
                      demonstrating our commitment to transparency, integrity, and exceptional client satisfaction.
                    </p>
                    <p className="text-blue-600 text-sm font-medium hover:underline">
                      Click to verify our BBB accreditation →
                    </p>
                  </div>
                </div>
              </a>
            </div>
          </div>
          
          {/* Payment Methods */}
          <div className="border-t border-gray-800 pt-6">
            <div className="flex justify-center items-center space-x-6 mb-6">
              <p className="text-gray-400 text-sm">We Accept:</p>
              <div className="flex items-center space-x-4">
                {/* Visa Logo - High Quality */}
                <div className="bg-white px-6 py-3 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <svg className="h-10 w-16" viewBox="0 0 78 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M31.2 20.48L33.92 3.52H38.4L35.68 20.48H31.2ZM54.56 3.84C53.68 3.52 52.32 3.12 50.56 3.12C46.24 3.12 43.2 5.36 43.2 8.64C43.2 11.04 45.36 12.32 46.96 13.12C48.64 13.92 49.2 14.48 49.2 15.28C49.2 16.48 47.76 17.04 46.4 17.04C44.48 17.04 43.44 16.72 41.92 16L41.28 15.68L40.56 19.68C41.68 20.16 43.76 20.64 45.92 20.64C50.56 20.64 53.52 18.4 53.6 14.96C53.6 13.04 52.24 11.6 49.28 10.48C47.76 9.76 46.88 9.28 46.88 8.4C46.88 7.6 47.84 6.72 49.92 6.72C51.36 6.72 52.48 7.04 53.36 7.36L53.84 7.6L54.56 3.84ZM62.24 13.76C62.64 12.72 64.32 8.16 64.32 8.16C64.32 8.24 64.72 7.12 64.96 6.48L65.28 8.08C65.28 8.08 66.24 12.56 66.4 13.76H62.24ZM68.24 3.52H64.96C63.92 3.52 63.12 3.76 62.64 4.88L55.36 20.48H59.92C59.92 20.48 60.72 18.32 60.88 17.92C61.52 17.92 66 17.92 66.8 17.92C66.96 18.48 67.36 20.48 67.36 20.48H71.36L68.24 3.52ZM26.4 3.52L22 15.04L21.52 12.72C20.72 10.16 18.24 7.36 15.52 5.92L19.52 20.48H24.16L31.04 3.52H26.4Z" fill="#1434CB"/>
                    <path d="M18.08 3.52H10.72L10.64 3.92C16.16 5.2 20 8.32 21.52 12.72L20 4.88C19.76 3.84 18.96 3.52 18.08 3.52Z" fill="#F7B600"/>
                  </svg>
                </div>
                {/* MasterCard Logo - High Quality */}
                <div className="bg-white px-6 py-3 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <svg className="h-10 w-16" viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g>
                      <circle cx="15" cy="16" r="10" fill="#EB001B"/>
                      <circle cx="33" cy="16" r="10" fill="#F79E1B"/>
                      <path d="M24 9.6C22.4 11.2 21.4 13.5 21.4 16C21.4 18.5 22.4 20.8 24 22.4C25.6 20.8 26.6 18.5 26.6 16C26.6 13.5 25.6 11.2 24 9.6Z" fill="#FF5F00"/>
                    </g>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p className="mb-2">
              © 2025 Sebastian Alliance Group LLC. All Rights Reserved.
            </p>
            <p className="mb-2">
              Estate Flow™ is a registered trademark of Sebastian Alliance Group LLC.
            </p>
            <p className="text-xs">
              <strong>LEGAL NOTICE:</strong> This software is proprietary and confidential. 
              Unauthorized access, use, reproduction, or distribution is strictly prohibited 
              and may result in severe civil and criminal penalties. All user data is encrypted 
              and protected under applicable data protection laws.
            </p>
            <p className="text-xs mt-2">
              Platform operated under financial services regulations. 
              Licensed and regulated in applicable jurisdictions.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
